/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import type { PhotoItem, WeddingStory } from "./types";

// High-end portfolio items featuring our generated assets + curated high-contrast editorial photography
export const PORTFOLIO_PHOTOS: PhotoItem[] = [
  {
    id: "photo-1",
    url: "/rose/demo/hero-vogue-wedding.png",
    title: "Le Chêne Noir",
    location: "Château de Villette, France",
    category: "editorial",
    camera: "Hasselblad H6D-100c",
    focalLength: "80mm f/2.2 HC",
    filmStock: "Monochrome High-Contrast (Leica)",
    narrative: "A dramatic composition capturing the architectural purity of high-fashion love. Styled in custom silk and bespoke wool tailoring against the weathered stone of historic France."
  },
  {
    id: "photo-2",
    url: "/rose/demo/amalfi-editorial-bride.png",
    title: "The Alabaster Veil",
    location: "Ravello, Amalfi Coast",
    category: "editorial",
    camera: "Leica M11-P Mono",
    focalLength: "50mm Leica Summilux ASPH",
    filmStock: "Editorial Matte Profile",
    narrative: "Commanding the Mediterranean golden hour. The bride stands elevated, enveloped in sunlight, with hand-draped silk fabric capturing the breeze of the Ravello cliffside."
  },
  {
    id: "photo-3",
    url: "/rose/demo/chateau-editorial-couple.png",
    title: "Quietude in Gold",
    location: "Provence Provence, France",
    category: "intimate",
    camera: "Hasselblad H6D-100c",
    focalLength: "80mm Zeiss Planar f/2.0",
    filmStock: "Golden Hour Satin Preset",
    narrative: "Intimate frame captured beside high archival French windows. Gentle natural light highlights the structured luxury design of the dress, letting shadows do the talking."
  },
  {
    id: "photo-4",
    url: "/rose/demo/ysl-afterparty-couple.png",
    title: "Midnight in Sparklers",
    location: "Onyx Salon, New York City",
    category: "afterparty",
    camera: "Leica M11-P",
    focalLength: "35mm Leica Summicron f/2.0",
    filmStock: "Direct Flash Luxury Preset",
    narrative: "Unscripted luxury. Vintage direct flash of the late-night celebration, capturing true uninhibited romance, vintage champagne, and tailored black tie elegance."
  },
  {
    id: "photo-5",
    url: "/rose/demo/solitaire-ring.jpg",
    title: "The Solitaire Ring",
    location: "Parisian Atelier, France",
    category: "details",
    camera: "Hasselblad H6D-100c",
    focalLength: "120mm Macro HC",
    filmStock: "Vibrant Classic Profile",
    narrative: "The timeless elegance of Parisian craft. Set on heavily textured handmade high-grade cardstock and raw silk fibers, showcasing intimate bespoke design elements."
  },
  {
    id: "photo-6",
    url: "/rose/demo/santal-table-scape.jpg",
    title: "Santal Table Scape",
    location: "Villa Monastero, Lake Como",
    category: "details",
    camera: "Hasselblad H6D-100c",
    focalLength: "105mm f/2.4",
    filmStock: "Warm Alabaster Satin",
    narrative: "A study in sophisticated dining design. Linen runners, vintage brass candlesticks, and structural white floristry capturing a serene lakeside seating layout."
  },
  {
    id: "photo-7",
    url: "/rose/demo/silk-trail.jpg",
    title: "The Silk Trail",
    location: "Venetian Grand Canal, Italy",
    category: "intimate",
    camera: "Leica SL3",
    focalLength: "50mm Summilux SL",
    filmStock: "Leica Summilux Premium",
    narrative: "A cinematic frame of the couple looking out from a raw mahogany water taxi. The long embroidered silk trail spills into the water reflection, telling a Parisian romance story."
  },
  {
    id: "photo-8",
    url: "https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?auto=format&fit=crop&w=1200&q=80",
    title: "Decadence Reflected",
    location: "Grand Hyatt Mansion, London",
    category: "afterparty",
    camera: "Leica SL3",
    focalLength: "65mm f/4.0",
    filmStock: "High Contrast Cinema Profile",
    narrative: "The dramatic reception lobby exit reflection. Captured with refined cinema tones, expressing neon ambient halos, tailored tuxedos, and intimate laughter amid high-society grandeur."
  }
];
// Rich, Vogue-like storytelling articles
export const WEDDING_STORIES: WeddingStory[] = [
  {
    id: "story-amalfi",
    couple: "Aurelia & Stefano",
    locationName: "Villa Cimbrone",
    region: "Ravello, Amalfi Coast",
    date: "September 14, 2025",
    quote: "We wanted something incredibly structural yet wild. Raw coastal granite paired with exquisite YSL tailoring.",
    narrativeIntro: "A high-fashion pilgrimage along the sheer cliffs of Southern Italy, where classical Roman architecture meets modern, boundary-pushing minimalism.",
    narrativeBody: "The union of Aurelia, an architectural lead, and Stefano, a luxury fashion consultant, could only exist in absolute visual purity. Captured entirely on high-resolution digital systems, we worked with natural shadows to outline the sweeping, hand-sewn organza layers of Aurelia's custom gown against Ravello's ancient stone balustrades. The sun hovered low, transforming the coastline into a high-contrast canvas where ivory silk, deep sand dunes, and sharp black wool merged. From the moment preparations began in the morning, there was a palpable feeling of calm and deep appreciation for the history of Villa Cimbrone. We took time to observe the way the northern light filtered through the gothic archways, catching the gentle lace details of her bridal veil. Our approach was highly collaborative; we spent hours walking through the gardens, documenting not just the grand vistas, but the small, quiet exchanges—a hand resting on a shoulder, the soft sound of silk rustling against antique gravel, and the silent laughter shared between the two of them during the terrace ceremony. As twilight descended over the Tyrrhenian Sea, the atmosphere shifted to a beautifully intimate al fresco dinner. Under a canopy of olive trees, guests gathered around a single hand-carved stone table flanked by hundreds of flickering beeswax candles. The absence of heavy artificial strobe rigs allowed the natural, warm candlelight to wrap the faces in classic, portrait-like tones. Every portrait taken during these late hours represents a sincere, unhurried study of connection and celebration.",
    paletteName: "Alabaster & Sand",
    paletteColors: ["#eae6df", "#c8b195", "#3a342e", "#1c1a17"],
    atmosphere: "Warm Cinematic, High Contrast, Quiet Romance",
    stylingDetails: {
      gowns: "Bespoke Schiaparelli Haute Couture with hand-draped organza wings",
      tailoring: "Saint Laurent (YSL) Grain de Poudre smoking tuxedo",
      floralDesign: "Sculptural single-stem white calla lilies bound in silk"
    },
    keyChapters: [
      { time: "11:00 AM", title: "Morning Stillness", description: "Minimalist portraiture inside the villa's frescoed master suite, focusing on delicate lace textures and negative space." },
      { time: "4:30 PM", title: "The Terrace Rite", description: "Bespoke exchanging of vows surrounded only by the horizon line of Ravello and the vast blue sea." },
      { time: "8:00 PM", title: "Al Fresco Under Olive Trees", description: "A quiet, candlelit long table detailed with raw porcelain platewares, linen napkins, and green olives." },
      { time: "11:30 PM", title: "The Midnight Spritz", description: "Candid direct flash coverage of a late-night private pool plunge in full tailoring." }
    ],
    coverUrl: "/rose/demo/amalfi-editorial-bride.png"
  },
  {
    id: "story-provence",
    couple: "Camille & Alexandre",
    locationName: "Château de Tourreau",
    region: "Provence, France",
    date: "June 28, 2025",
    quote: "The visual language was simple: classical order, natural light, and the raw texture of Southern France.",
    narrativeIntro: "An editorial feast of light and symmetry under the classical tall lavender-gilded avenues of a historic French chateau.",
    narrativeBody: "Provence is a study in soft contrasts. Camille wanted the photography to prioritize silence over noise. Inspired by mid-century French cinema, we leveraged soft, natural light spilling from 18th-century French windows. Every image utilizes premium digital configurations, rendering rich color plates where pale green sage, sun-bleached gravel, and gold trim paint a story of quiet, absolute luxury. We spent the days leading up to the celebration scouting the limestone pathways and ancient groves surrounding the château, noting how the sun dipped behind the lavender-scented fields at precisely 7:00 PM. This preparation allowed us to capture Camille and Alexandre in an environment that felt entirely natural to them. There were no forced poses; instead, we photographed them sharing a quiet bottle of wine before the guests arrived, laughing as they practiced their first dance in the empty courtyard, and taking slow, deliberate walks under the shade of centuries-old plane trees. During the evening, the celebration relaxed into an organic garden party. The gentle string trio blended seamlessly with the rustle of the leaves, and we focused on candid, unobtrusive close-ups of guests engaged in lively conversation, child-like laughter, and the amber glow of the setting sun on historic masonry. This collection of moments is a delicate, fine-art archive that feels both deeply grounded and incredibly elegant.",
    paletteName: "Santal & Sage",
    paletteColors: ["#ebe6dd", "#b2bfa9", "#a89a8a", "#0d0c0b"],
    atmosphere: "Classical Romance, Fine Art Aesthetics, Restrained Sage",
    stylingDetails: {
      gowns: "Custom Danielle Frankel high-neck lace crepe gown",
      tailoring: "Bespoke Anderson & Sheppard ivory double-breasted suit",
      floralDesign: "Garden roses in shades of sand, ivory, and dusty parchment"
    },
    keyChapters: [
      { time: "01:30 PM", title: "The Dressing Atelier", description: "Candid close-ups of silk ribbons, vintage pearl combs, and Alexandre's handwritten vows." },
      { time: "5:00 PM", title: "The Sacred Garden Vows", description: "A symmetrical woodland clearing surrounded by manicured boxwood hedges and ancient stone statues." },
      { time: "7:30 PM", title: "Cocktails by the Fountain", description: "Jazz trio performing softly as guests drink chilled Champagne beside old stone carvings." },
      { time: "10:00 PM", title: "The Chaperon Waltz", description: "A beautifully slow outdoor dance under a canopy of warm, suspended micro-bulbs." }
    ],
    coverUrl: "/rose/demo/chateau-editorial-couple.png"
  },
  {
    id: "story-loftysc",
    couple: "Vivienne & Julian",
    locationName: "Chelsea Powerhouse Loft",
    region: "SOHO, New York City",
    date: "October 11, 2025",
    quote: "A celebration of modern SOHO. Highly dynamic direct-flash portraits, industrial shadows, and unhinged energy.",
    narrativeIntro: "A love letter to metropolitan nightlife. Blending raw, brutalist brick structures with high-slit custom leather, lace, and diamonds.",
    narrativeBody: "Vivienne and Julian threw away the handbook. Taking inspiration from 90s vintage couture styling, we shot their wedding with high-contrast exposures, capturing rich details with our digital layouts. The venue comprised raw black industrial beams, polished concrete floors, hundreds of pillar candles, and a soaring glass ceiling overlooking the Manhattan skyline. Before the evening ceremony, we took Vivienne and Julian out onto the crowded streets of SOHO. Moving through the historic cast-iron district, we embraced the real, chaotic energy of New York City—using passing yellow taxis, deep metropolitan shadows, and streetlamps to frame their bold attire. This city walk was entirely unscripted; we simply walked with them as they shared a private laugh, held hands across busy intersections, and stood under the glowing neon signs of their favorite local corners. When we returned to the loft for the vows, the transition was breathtaking. The space was transformed into a quiet sanctuary illuminated entirely by the warmth of hundreds of pillar candles. We adjusted our digital systems to handle the extreme low-light scenario, preserving the rich, deep textures of the raw brick walls and the soft, glowing skin tones of the couple. The resulting frames feel raw, intimate, and wonderfully alive, capturing an afterparty that went late into the night, filled with champagne, leather jackets, and uninhibited celebration.",
    paletteName: "Onyx & Champagne",
    paletteColors: ["#ffffff", "#f5eedc", "#2d2d2d", "#080808"],
    atmosphere: "Edgy Noir, Direct Flash, Brutalist Elegance",
    stylingDetails: {
      gowns: "Custom Vivienne Westwood silk corset top with tailored wide-leg pants, transitioning into a modern high-slit silk gown",
      tailoring: "Sleek tailored Tom Ford modern peak-lapel onyx tuxedo",
      floralDesign: "Deep red calla lilies and single black roses in raw concrete urns"
    },
    keyChapters: [
      { time: "03:00 PM", title: "SOHO Street Promenade", description: "Street-level editorial walk capturing realistic movement through Manhattan's historic crosswalks." },
      { time: "6:30 PM", title: "The Candlelit Union", description: "Exchanging of platinum bands in a dark loft, illuminated exclusively by three hundred wax pillar candles." },
      { time: "9:00 PM", title: "The Oyster & Caviar Bar", description: "High-contrast culinary storytelling featuring crystal glassware, premium seafood, and dynamic laughter." },
      { time: "01:00 AM", title: "Afterparty Burn", description: "Vaporous flash frame shots capturing champagne sprays, custom leather jackets, and street taxi escapes." }
    ],
    coverUrl: "/rose/demo/ysl-afterparty-couple.png"
  }
];

// Luxury options for Moodboard Builder
export const MOODBOARD_OPTIONS = {
  palettes: [
    { id: "santal", name: "Santal & Alabaster", scale: ["#FAF9F6", "#ECE9E2", "#DCD5C9", "#1A1A1A"], description: "Soft bone white, raw oatmeal linen, and organic charcoal. Warm, luxury minimalism." },
    { id: "onyx", name: "Onyx & Champagne", scale: ["#FFFFFF", "#F3ECE0", "#8A7D6B", "#0D0D0D"], description: "Stark contrast. Brilliant luxury silk, warm gold champagne, and absolute onyx black." },
    { id: "verdant", name: "Verdant & Cream", scale: ["#FDFBF7", "#E6EADC", "#828D75", "#2B3125"], description: "Elegant European estate notes. Ivory plaster, dusty rosemary leaf, and deep forest pine." },
    { id: "terracotta", name: "Faded Terracotta & Dust", scale: ["#FAF8F5", "#E8DFD0", "#C99A82", "#3D2B24"], description: "Warm Mediterranean bricks, bleached stone surfaces, and antique clay highlights." }
  ],
  styles: [
    { id: "vogue", name: "Vogue High-Fashion", detail: "Dramatic direct shadows, sculpted posing, wide angles, high magazine editorial density." },
    { id: "noir", name: "Cinematic Noir", detail: "Heavy shadow plays, deep motion blur, cinematic 2.35:1 vintage crops, romantic low-light grain." },
    { id: "candid", name: "Modern Art Journal", detail: "Unscripted frame movements, warm heritage colors, polished digital grain, raw emotional storytelling." },
    { id: "classical", name: "Fine Art Classical", detail: "Soft natural northern light, balanced symmetry, elegant stillness, medium-format painterly focus." }
  ],
  settings: [
    { id: "coastline", name: "Amalfi Coast Terraces", detail: "Sheer cliffs, stone balustrades, limitless azure blue lines, and warm stone reflective sunlight." },
    { id: "chateau", name: "French Historic Château", detail: "Ornate gold moldings, high arched windows, weathered symmetrical facades, and lavender-scented estate trails." },
    { id: "loft", name: "SOHO Brutalist Loft", detail: "Polished concrete, exposed steel girders, Manhattan sky, direct studio flash grids." },
    { id: "estate", name: "English Countryside Manors", detail: "Overgrown ivy brick masonry, green rose glasshouses, antique libraries, and soft misty mornings." }
  ]
};
